/**
 * ADR 115 Phase 4 — `redirect_url` plumbing for sms-web.
 *
 * The backend augments the login and refresh responses with an
 * optional `redirect_url` field when the request hostname doesn't match
 * the signed-in tenant's brand-correct host (e.g. a SAFE+ tenant signing
 * in at `sms.{env}.aegix.global` should land on `safe-ms.{env}.aegix.global`).
 * The field is **optional** — omitted (never null) when no redirect is
 * needed, so backends without the field and same-brand hosts simply yield
 * `undefined` and the existing flow is preserved.
 *
 * Per ADR 115 §6 / Phase 4 the two surfaces behave differently:
 *   - **Login** → navigate immediately (`window.location.assign`). The
 *     user just authenticated; there's no in-progress work to disrupt.
 *   - **Refresh** → graceful interstitial; do NOT auto-navigate. A
 *     mid-session brand flip shouldn't yank an admin out of an active
 *     screen, so we surface a prompt and let them choose when to switch.
 *
 * This module is the sms-web-local copy of the redirect plumbing. As with
 * `getBrandKeyFromAuthCookie` (duplicated, not shared), aim-web keeps its
 * own copy in `apps/aegix-aim-react` rather than extracting a shared lib —
 * the two apps' login/provider wiring diverges enough that a shared seam
 * would cost more than the duplication.
 *
 * Session continuity across the host switch: per ADR-118 Phase 2 the
 * access session cookie is host-only and per-app-named (`jwt-auth-sms`
 * here) — it does NOT carry across
 * `sms.*` → `safe-ms.*`. Cross-host continuity rides the HttpOnly
 * parent-domain `aegix-sso` assertion instead, consumed at boot on the
 * target host by the auth lib's SSO bootstrap. We never clear
 * storage/cookies on redirect — the target host re-establishes its
 * own session.
 */

import { StorageKeys } from '@aegix/auth-core';
import type { SMSUser } from './sms-auth';

/**
 * Read the optional `redirect_url` off a value that may carry it.
 *
 * The login response is unpacked by `@aegix/auth-identity`'s
 * `unpackLoginResult`, which strips `jwt` / `refresh_token` /
 * `permissions_version` and spreads the remaining response fields onto
 * the user object verbatim (`SimpleJwtUser` has an index signature). So a
 * login-time `redirect_url` rides through on `user['redirect_url']`.
 *
 * Returns the trimmed string when present and non-empty, else `undefined`.
 * Defensive about non-string shapes — a malformed field degrades to "no
 * redirect" rather than throwing.
 */
export function getRedirectUrl(source: unknown): string | undefined {
  if (source === null || typeof source !== 'object') return undefined;
  const value = (source as Record<string, unknown>)['redirect_url'];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Decide whether `redirect_url` points somewhere we should actually
 * navigate to. True only when it parses to an absolute URL whose origin
 * differs from the current one — guards against a redirect loop if the
 * backend ever echoes the host we're already on, and against a malformed
 * (non-absolute) value.
 *
 * SSR / no-`window` contexts return false (nothing to navigate).
 */
export function shouldRedirect(redirectUrl: string): boolean {
  if (typeof window === 'undefined') return false;
  let target: URL;
  try {
    target = new URL(redirectUrl);
  } catch {
    // Not an absolute URL — refuse to navigate to an ambiguous target.
    return false;
  }
  return target.origin !== window.location.origin;
}

/**
 * Perform the brand-correct host switch. Uses `window.location.assign`
 * (a full navigation, not an SPA route change) because the target is a
 * different origin. The session is re-established on the target host via
 * the parent-domain HttpOnly `aegix-sso` assertion (SSO bootstrap) — the
 * host-only `jwt-auth-sms` cookie does not carry across — so we
 * deliberately do NOT touch storage or cookies.
 *
 * No-op (returns false) when there's no `window` or the target isn't a
 * cross-origin absolute URL — callers can treat a `false` return as
 * "stay put".
 */
export function performBrandRedirect(redirectUrl: string): boolean {
  if (!shouldRedirect(redirectUrl)) return false;
  window.location.assign(redirectUrl);
  return true;
}

// ---------------------------------------------------------------------------
// Refresh-flow redirect channel.
//
// Unlike login, a refresh-time `redirect_url` is NOT visible on the
// provider's user object: `@aegix/auth-identity`'s `refresh_success` only
// rotates the JWT, and sms-web's own token-refresh callback
// (`lib/http-client.ts`) historically returned just `{ token }`. So we
// publish a refresh-time `redirect_url` through this tiny module-local
// pub/sub — the same shape as the lib's `onTokenRefreshChange` listeners —
// and the `BrandRedirectGate` subscribes to render the interstitial.
//
// Deliberately app-local (not a shared lib) and dependency-free.
// ---------------------------------------------------------------------------

type BrandRedirectListener = (redirectUrl: string) => void;

const refreshRedirectListeners = new Set<BrandRedirectListener>();

/**
 * Subscribe to refresh-flow brand-redirect events. Returns an
 * unsubscribe function. Used by `BrandRedirectGate`.
 */
export function onRefreshRedirect(listener: BrandRedirectListener): () => void {
  refreshRedirectListeners.add(listener);
  return () => {
    refreshRedirectListeners.delete(listener);
  };
}

/**
 * Publish a refresh-flow brand-redirect. Called from the sms-web token
 * refresh path (`lib/http-client.ts`) when a refresh response carries a
 * `redirect_url`. Filtered through `shouldRedirect` so same-origin /
 * malformed values never raise an interstitial.
 */
export function publishRefreshRedirect(redirectUrl: string): void {
  if (!shouldRedirect(redirectUrl)) return;
  for (const listener of refreshRedirectListeners) {
    listener(redirectUrl);
  }
}

/**
 * Test-only: drop all refresh-redirect subscribers so a fresh test starts
 * from a clean channel. Not part of the public surface.
 *
 * @internal
 */
export function _resetRefreshRedirectListenersForTests(): void {
  refreshRedirectListeners.clear();
}

// Re-exported so the gate (and tests) can read the access-token slot if
// they ever need to assert local session state around a redirect (the
// session itself is re-established on the target host via the `aegix-sso`
// SSO bootstrap — see the header note). Kept here to keep the redirect
// concern's imports in one module.
export { StorageKeys };
export type { SMSUser };
